import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom';
import { all_routes } from '../../router/all_routes';
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from '../../../core/common/collapse-header/collapse-header';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../core/data/redux/store';
import { exportToCSV, exportToExcel, exportToPDF } from '../../../core/common/GlobalFunctions/exportTableData';
import { createClient, deleteClient, fetchAllClients, fetchClientSummary, updateClient } from '../../../core/data/redux/projects/client/clientAction';
import DeleteModal1 from '../../../core/modals/deleteModal1';
import { filterData } from '../../../core/common/GlobalFunctions/tableFilters';
import { Drawer, message } from 'antd';
import { status as DepartmentStatus } from '../../../core/common/selectoption/selectoption';
import { decryptData } from '../../../core/utils/encryptUtils';

const ClientList = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedStatus, setSelectedStatus] = useState('active');
    const [selectedSort, setSelectedSort] = useState<any>("");
    const [tableKey, setTableKey] = useState(0);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [modalType, setModalType] = useState<'edit' | 'create' | ''>('');
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const { user } = useSelector((state: any) => state.auth);
    const { clients, clientSummary, loading } = useSelector((state: any) => state.project.clients);

    const industryOptions = [
        "Manufacturing",
        "Retail",
        "IT Services",
        "Consulting",
        "Healthcare",
        "Finance",
        "Education",
        "Hospitality",
        "Others"
    ];


    // Permission
    const encryptedPermissions = localStorage.getItem('currentPermissions');
    const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

    const canAdd =
        permissions.includes("Add") || permissions.includes("AllowAll");
    const canView =
        permissions.includes("View") || permissions.includes("AllowAll");
    const canEdit =
        permissions.includes("Edit") || permissions.includes("AllowAll");
    const canDelete =
        permissions.includes("Delete") || permissions.includes("AllowAll");
    const canExport =
        permissions.includes("Export") || permissions.includes("AllowAll");

    const [clientFormData, setClientFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        company: '',
        company_address: '',
        industry_type: '',
        gstin: '',
        status: 'active'
    });
    const [validationErrors, setValidationErrors] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        company: '',
        company_address: '',
        industry_type: '',
        gstin: '',
        status: ''
    });

    const defaultFormData = {
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        company: '',
        company_address: '',
        industry_type: '',
        gstin: '',
        status: ''
    };
    const resetForm = () => {
        setClientFormData(defaultFormData);
        setValidationErrors(defaultFormData);
    };

    useEffect(() => {
        if (!clients.length) {
            dispatch(fetchAllClients())
        }
        if (!clientSummary?.total_clients) {
            dispatch(fetchClientSummary())
        }
    }, [dispatch])

    const showModal = (type: 'edit' | 'create') => {
        setModalType(type);
        setIsModalVisible(true);
    };
    const handleCancel = () => {
        setIsModalVisible(false);
        setModalType('');
        resetForm();
    };

    const handleEditClient = (record: any): void => {
        setSelectedRowData(record);
        setClientFormData({
            first_name: record.first_name,
            last_name: record.last_name,
            email: record.email,
            phone_number: record.phone_number,
            company: record.company,
            company_address: record.company_address,
            industry_type: record.industry_type,
            gstin: record.gstin,
            status: record.status
        });
        showModal('edit');
    };
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setClientFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };
    const handleSubmit = async () => {
        const errors: any = {};

        const nameRegex = /^[A-Za-z\s]+$/;

        // Basic validation
        if (!clientFormData.first_name) {
            errors.first_name = 'First Name is required';
        } else if (!nameRegex.test(clientFormData.first_name)) {
            errors.first_name = 'First Name can only contain letters and spaces';
        }
        if (clientFormData.last_name && !nameRegex.test(clientFormData.last_name)) {
            errors.last_name = 'Last Name can only contain letters and spaces';
        }
        if (!clientFormData.email) errors.email = 'Email is required';
        if (!clientFormData.phone_number) errors.phone_number = 'Phone Number is required';
        if (!clientFormData.company) errors.company = 'Company is required';
        if (!clientFormData.status) errors.status = 'Status is required';

        // Additional validations if necessary
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (clientFormData.email && !emailRegex.test(clientFormData.email)) {
            errors.email = 'Invalid email format';
        }

        const phoneRegex = /^[0-9]{10}$/;
        if (clientFormData.phone_number && !phoneRegex.test(clientFormData.phone_number)) {
            errors.phone_number = 'Invalid phone number format';
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }
        const updatedFormData = {
            ...clientFormData,
            created_by: modalType === 'create' ? user.UserId : undefined,
            modified_by: modalType === 'edit' ? user.UserId : undefined,
            client_or_vendor: 0,
        };
        let result;

        if (modalType === 'create') {
            result = await dispatch(createClient(updatedFormData));
            if (result.meta.requestStatus === 'fulfilled') {
                message.success('Client created successfully');
            }
        } else if (modalType === 'edit' && selectedRowData?.client_id) {
            result = await dispatch(updateClient({ clientId: selectedRowData.client_id, ...updatedFormData }));
            if (result.meta.requestStatus === 'fulfilled') {
                message.success('Client updated successfully');
            }
        }
        if (result?.meta.requestStatus === 'fulfilled') {
            await dispatch(fetchAllClients());
            await dispatch(fetchClientSummary());
            setTableKey((prevKey) => prevKey + 1);
            resetForm();
        } else {
            message.error(result?.payload.error);
        }
        handleCancel();
    };

    const handleDeleteClient = async (clientId: number) => {
        try {
            const result = await dispatch(deleteClient({ clientId }));
            if (result.meta.requestStatus === 'fulfilled') {
                message.success('Client deleted successfully');
                await dispatch(fetchAllClients());
                await dispatch(fetchClientSummary());
                setTableKey((prevKey) => prevKey + 1);
                setIsDeleteModalVisible(false);
            } else {
                alert(result.payload.error);
            }
        } catch (error) {
            alert('Something went wrong');
        }
    };

    const exportData: any = clients
        ? clients
            .filter((client: any) => client.client_or_vendor === 0)
            .map((client: any) => ({
                FirstName: client.first_name,
                LastName: client.last_name,
                Email: client.email,
                Phone: client.phone_number,
                Company: client.company,
                Address: client.company_address,
                Industry: client.industry_type,
                GSTIN: client.gstin,
                Status: client.status,
            }))
        : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const data: any = clients ? clients.filter((client: any) => client.client_or_vendor === 0) : [];



    const filteredData = useMemo(() => {
        const filters =
            selectedStatus && selectedStatus !== "All"
                ? { status: selectedStatus }
                : {};
        return filterData(data || [], filters, "created_at", "created_at", selectedSort);
    }, [data, selectedStatus, selectedSort]);

    useEffect(() => {
        setTableKey(prevKey => prevKey + 1);
    }, [selectedStatus, selectedSort, clients]);


    const columns = [
        {
            title: "Client Name",
            dataIndex: "first_name",
            render: (text: string, record: any) => (
                <div className="d-flex align-items-center file-name-icon">
                    {/* <Link
                        to={all_routes.clientdetils}
                        className="avatar avatar-md border avatar-rounded"
                    >
                        <ImageWithBasePath src={`assets/img/users/${record.Image}`} className="img-fluid" alt="img" />
                    </Link> */}
                    <div className="ms-2">
                        <h6 className="fw-medium">
                            <div>{record.first_name}{" "}{record.last_name}</div>
                        </h6>
                        <span className="fs-12 fw-normal ">{record.Work}</span>
                    </div>
                </div>
            ),
            sorter: (a: any, b: any) => a.first_name.length - b.first_name.length,
        },
        {
            title: "Client Code",
            dataIndex: "clientCode",
            sorter: (a: any, b: any) => a.clientCode.length - b.clientCode.length,
        },
        {
            title: "Company Name",
            dataIndex: "company",
            sorter: (a: any, b: any) => a.company.length - b.company.length,
        },
        {
            title: "Email",
            dataIndex: "email",
            sorter: (a: any, b: any) => a.email.length - b.email.length,
        },
        {
            title: "Phone",
            dataIndex: "phone_number",
            sorter: (a: any, b: any) => a.phone_number.length - b.phone_number.length,
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text: string, record: any) => (
                <span className={`badge ${text === 'active' ? 'badge-success' : 'badge-danger'} d-inline-flex align-items-center badge-xs`}>
                    <i className="ti ti-point-filled me-1" />
                    {text ? text.charAt(0).toUpperCase() + text.slice(1) : ""}

                </span>

            ),
        },
        {
            title: "Action",
            dataIndex: "actions",
            render: (text: string, record: any) => (
                <div className="action-icon d-inline-flex">
                    <button
                        className="me-2"
                        onClick={() => { if (!canEdit) return; showModal("edit"); handleEditClient(record); }}
                        disabled={!canEdit}
                        style={{ cursor: canEdit ? "pointer" : "not-allowed", opacity: canEdit ? 1 : 0.5, }}
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title={!canEdit ? "Permission Denied" : "Edit Client"}
                    >
                        <i className="ti ti-edit" />
                    </button>

                    <button
                        onClick={() => { if (!canDelete || record.status === "inactive") return; setSelectedRowData(record); setIsDeleteModalVisible(true); }}
                        className="me-2"
                        disabled={!canDelete || record.status === "inactive"}
                        style={{ cursor: !canDelete || record.status === "inactive" ? "not-allowed" : "pointer", opacity: !canDelete || record.status === "inactive" ? 0.5 : 1, }}
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title={!canDelete ? "Permission Denied" : record.status === "inactive" ? "Client is inactive" : "Delete Client"}
                    >
                        <i className="ti ti-trash" />
                    </button>
                </div>
            ),
        },
    ]

    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Clients</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={all_routes.adminDashboard}>
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">Projects</li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Client List
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            {/* <div className="me-2 mb-2">
                                <div className="d-flex align-items-center border bg-white rounded p-1 me-2 icon-list">
                                    <Link
                                        to={all_routes.clientlist}
                                        className="btn btn-icon btn-sm active bg-primary text-white me-1"
                                    >
                                        <i className="ti ti-list-tree" />
                                    </Link>
                                    <Link to={all_routes.clientgrid} className="btn btn-icon btn-sm">
                                        <i className="ti ti-layout-grid" />
                                    </Link>
                                </div>
                            </div> */}
                            {canExport && (
                                <div className="me-2 mb-2">
                                    <div className="dropdown">
                                        <Link
                                            to="#"
                                            className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                                            data-bs-toggle="dropdown"
                                        >
                                            <i className="ti ti-file-export me-1" />
                                            Export
                                        </Link>
                                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                                            <li>
                                                <button
                                                    onClick={() => exportToCSV(exportData, 'clients_data')}
                                                    className="dropdown-item rounded-1"
                                                >
                                                    <i className="ti ti-file-type-pdf me-1" />
                                                    Export as CSV
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    onClick={() => exportToExcel(exportData, 'clients_data')}
                                                    className="dropdown-item rounded-1"
                                                >
                                                    <i className="ti ti-file-type-xls me-1" />
                                                    Export as Excel{" "}
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    onClick={() => exportToPDF(exportData, 'clients_data')}
                                                    className="dropdown-item rounded-1"
                                                >
                                                    <i className="ti ti-file-type-xls me-1" />
                                                    Export as PDF{" "}
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            )}
                            {canAdd && (
                                <div className="mb-2">
                                    <button
                                        onClick={() => showModal('create')}
                                        className="btn btn-primary d-flex align-items-center"
                                    >
                                        <i className="ti ti-circle-plus me-2" />
                                        Add Client
                                    </button>
                                </div>
                            )}
                            <div className="ms-2 head-icons">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>
                    {/* /Breadcrumb */}
                    {/* Clients Info */}
                    <div className="row">
                        <div className="col-xl-3 col-md-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0 me-2">
                                                <span className="p-2 br-10 bg-pink-transparent border border-pink d-flex align-items-center justify-content-center">
                                                    <i className="ti ti-users-group text-pink fs-18" />
                                                </span>
                                            </div>
                                            <div>
                                                <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                                                    Total Clients
                                                </p>
                                                <h4>{clientSummary?.total_clients}</h4>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-md-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0 me-2">
                                                <span className="p-2 br-10 bg-success-transparent border border-success d-flex align-items-center justify-content-center">
                                                    <i className="ti ti-user-share fs-18" />
                                                </span>
                                            </div>
                                            <div>
                                                <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                                                    Active Clients
                                                </p>
                                                <h4>{clientSummary?.active_clients}</h4>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-md-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0 me-2">
                                                <span className="p-2 br-10 bg-danger-transparent border border-danger d-flex align-items-center justify-content-center">
                                                    <i className="ti ti-user-pause fs-18" />
                                                </span>
                                            </div>
                                            <div>
                                                <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                                                    Inactive Clients
                                                </p>
                                                <h4>{clientSummary?.inactive_clients}</h4>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-md-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0 me-2">
                                                <span className="p-2 br-10 bg-info-transparent border border-info d-flex align-items-center justify-content-center">
                                                    <i className="ti ti-user-plus fs-18" />
                                                </span>
                                            </div>
                                            <div>
                                                <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                                                    New Clients
                                                </p>
                                                <h4>{clientSummary?.new_clients}</h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Clients Info */}
                    {/* Clients list */}
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <h5>Client List</h5>
                            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                                <div className="dropdown me-3">
                                    <Link
                                        to="#"
                                        className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                                        data-bs-toggle="dropdown"
                                    >
                                        {selectedStatus ? selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1) : "Select Status"}
                                    </Link>
                                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                                        <li>
                                            <Link
                                                to="#"
                                                onClick={() => setSelectedStatus("All")}
                                                className="dropdown-item rounded-1"
                                            >
                                                All
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                to="#"
                                                onClick={() => setSelectedStatus('active')}
                                                className="dropdown-item rounded-1"
                                            >
                                                Active
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                to="#"
                                                onClick={() => setSelectedStatus('inactive')}
                                                className="dropdown-item rounded-1"
                                            >
                                                Inactive
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                                <div className="dropdown">
                                    <Link
                                        to="#"
                                        className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                                        data-bs-toggle="dropdown"
                                    >
                                        {selectedSort ? `Sort By : ${selectedSort}` : "Sort By : Last 7 Days"}
                                    </Link>
                                    <ul className="dropdown-menu dropdown-menu-end p-3">
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Recently Added")} className="dropdown-item rounded-1">
                                                Recently Added
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Ascending")} className="dropdown-item rounded-1">
                                                Ascending
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Descending")} className="dropdown-item rounded-1">
                                                Descending
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Last Month")} className="dropdown-item rounded-1">
                                                Last Month
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Last 7 Days")} className="dropdown-item rounded-1">
                                                Last 7 Days
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={filteredData} columns={columns} Selection={false} />
                        </div>
                    </div>

                    {/* /Clients list */}
                </div>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>
            {/* /Page Wrapper */}
            {/* Add Client */}
            <Drawer
                title={modalType === "edit" ? "Edit Client" : "Add Client"}
                placement="right"
                width={600}
                onClose={handleCancel}
                visible={isModalVisible}
                style={{ marginTop: 50, paddingBottom: 50 }}
            >
                <form onSubmit={(e) => e.preventDefault()}>
                    <div className="tab-content" id="myTabContent">
                        <div
                            className="tab-pane fade show active"
                            tabIndex={0}
                        >
                            <div className="modal-body pb-0 ">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                First Name <span className="text-danger"> *</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="first_name"
                                                value={clientFormData.first_name}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.first_name && (
                                                <small className="text-danger">{validationErrors.first_name}</small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Last Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="last_name"
                                                value={clientFormData.last_name}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.last_name && (
                                                <small className="text-danger">{validationErrors.last_name}</small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                Email<span className="text-danger"> *</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="email"
                                                value={clientFormData.email}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.email && (
                                                <small className="text-danger">{validationErrors.email}</small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                Phone Number <span className="text-danger"> *</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="phone_number"
                                                value={clientFormData.phone_number}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.phone_number && (
                                                <small className="text-danger">{validationErrors.phone_number}</small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Company<span className="text-danger"> *</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="company"
                                                value={clientFormData.company}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.company && (
                                                <small className="text-danger">{validationErrors.company}</small>
                                            )}
                                        </div>
                                    </div>

                                    {/* New fields */}
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Company Address</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="company_address"
                                                value={clientFormData.company_address}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.company_address && (
                                                <small className="text-danger">{validationErrors.company_address}</small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Industry Type</label>

                                            {clientFormData.industry_type === "Others" ||
                                                (clientFormData.industry_type && !industryOptions.includes(clientFormData.industry_type)) ? (
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="industry_type"
                                                    placeholder="Enter your industry"
                                                    value={industryOptions.includes(clientFormData.industry_type) ? "" : clientFormData.industry_type}
                                                    onChange={(e) => setClientFormData({ ...clientFormData, industry_type: e.target.value })}
                                                    onBlur={(e) => {
                                                        if (!e.target.value.trim()) {
                                                            setClientFormData({ ...clientFormData, industry_type: "" });
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <select
                                                    className="form-select"
                                                    name="industry_type"
                                                    value={clientFormData.industry_type}
                                                    onChange={(e) => setClientFormData({ ...clientFormData, industry_type: e.target.value })}
                                                >
                                                    <option value="">-- Select Industry --</option>
                                                    {industryOptions.map((i) => (
                                                        <option key={i} value={i}>{i}</option>
                                                    ))}
                                                </select>
                                            )}

                                            {validationErrors.industry_type && (
                                                <small className="text-danger">{validationErrors.industry_type}</small>
                                            )}
                                        </div>


                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">GSTIN</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="gstin"
                                                value={clientFormData.gstin}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.gstin && (
                                                <small className="text-danger">{validationErrors.gstin}</small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-12">
                                        <div className="mb-3">
                                            <label className="form-label">Status <span className="text-danger"> *</span></label>
                                            <select
                                                className="form-control"
                                                name="status"
                                                value={clientFormData.status}
                                                onChange={handleInputChange}
                                            >
                                                {DepartmentStatus?.map((status: any) => (
                                                    <option key={status.value} value={status.value}>
                                                        {status.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {validationErrors.status && (
                                                <small className="text-danger">{validationErrors.status}</small>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-outline-light border me-2"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </button>
                                <button type="button" onClick={handleSubmit} disabled={loading}
                                    className="btn btn-primary">
                                    {loading && <span className="spinner-border spinner-border-sm me-2" />}
                                    {modalType === 'edit' ? 'Update Client' : 'Add Client'}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </Drawer >
            {/* /Add Client */}

            < DeleteModal1
                isVisible={isDeleteModalVisible}
                onClose={() => setIsDeleteModalVisible(false)}
                onConfirm={() => selectedRowData?.client_id && handleDeleteClient(selectedRowData.client_id)}
            />
        </>

    )
}

export default ClientList
